import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { emptyDivTestId } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  VMDB_PDB_DELETE_POLL_DELAY,
  VMDB_PDB_DELETE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationConfirmationProps } from "../OperationTypes";

export interface VmDbDeletePdbProps extends OperationConfirmationProps {
  databaseId: string | undefined;
}

const VmDbDeletePdb = (
  { location, databaseId, onExecute }: VmDbDeletePdbProps,
): JSX.Element => {
  const { invokeCall: deletePdb } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.deleteVmdbPluggableDatabase },
  );

  React.useEffect(() => {
    const { subscriptionId, resourceGroup, resourceName: pdbName } = parseId(databaseId);
    const resGroup = decodeURIComponent(resourceGroup || "");

    const invokePdbOptions = {
      onSuccess: onExecute,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.deletePluggableDb(),
          message: Messages.notifications.inProgress.messages.deletePluggableDb(pdbName),
        },
        success: {
          title: Messages.notifications.success.titles.deletePluggableDb(),
          message: Messages.notifications.success.messages.deletePluggableDb(pdbName),
        },
        failure: {
          title: Messages.notifications.failure.titles.delete(),
          message: Messages.notifications.failure.messages.deletePluggableDb(pdbName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.VMDB_PDB_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            pluggableDatabaseName: pdbName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.VMDB_DELETED_CHECK_PDB,
          errorStatusOverrides: [404],
          delay: VMDB_PDB_DELETE_POLL_DELAY,
          interval: VMDB_PDB_DELETE_POLL_INTERVAL,

        },
      },
    };

    deletePdb({
      subscriptionId,
      resourceGroupName: resGroup,
      pluggableDatabaseName: pdbName,
    }, invokePdbOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div data-test-id={emptyDivTestId} />;
};

export const newVmDbDeletePdb = (
  props: VmDbDeletePdbProps,
): JSX.Element => (<VmDbDeletePdb {...props} />);
